import { useState } from 'react';
import { Button, Drawer, Modal, Space, Typography, message } from 'antd';
import { ExclamationCircleOutlined, SettingOutlined, BarChartOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { OrderChannel } from '@erpgold/shared';
import { PageShell } from '@/components/PageShell/PageShell';
import { ERPTable } from '@/components/DataTable/ERPTable';
import { FilterBar } from '@/components/FilterBar/FilterBar';
import { useAuth } from '@/hooks/useAuth';
import { useListPageState } from '@/hooks/useListPageState';
import { useOrderList } from '@/hooks/useOrderList';
import { useCancelOrder } from '@/hooks/useOrderMutations';
import { useStoreSettings } from '@/hooks/useStoreSettings';
import type { TenantOrder } from '@/mocks/orders';
import { ROUTES } from '@/constants/routes';
import { orderColumns, buildOrderActionColumn } from './parts/columns';
import { buildOrderFilters, ORDER_FILTER_KEYS, type OrderFilterKey } from './parts/filters';
import type { OrderChannelGroup } from './parts/constants';
import { OrderSummaryCards } from './components/OrderSummaryCards';
import { ExportOrderButton } from './components/ExportOrderButton';
import { printOrderReceipt } from './utils/printReceipt';
import { OrderShiftSummary } from './components/OrderShiftSummary';

const { Text } = Typography;

export function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    page,
    pageSize,
    setTableState,
    filters,
    drafts,
    setDraft,
    applyFilters,
    clearFilters,
    activeFilterCount,
    selectedRowKeys,
    setSelectedRowKeys,
    clearSelection,
  } = useListPageState<OrderFilterKey>({
    pageKey: 'orders-list',
    filterKeys: ORDER_FILTER_KEYS,
    userId: user?.id,
    defaultFilters: {
      date_from: dayjs().startOf('day').toISOString(),
      date_to: dayjs().endOf('day').toISOString(),
    },
  });

  const [openColumnSettings, setOpenColumnSettings] = useState<(() => void) | null>(null);
  const [shiftSummaryOpen, setShiftSummaryOpen] = useState(false);
  const cancelMutation = useCancelOrder();

  const { data, isFetching } = useOrderList({
    page,
    page_size: pageSize,
    channel_group: filters.channel_group || undefined,
    status_group: filters.status_group || undefined,
    search: filters.search || undefined,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleCancelOrder = (row: TenantOrder) => {
    let reason = '';
    Modal.confirm({
      title: `Huỷ đơn ${row.order_number}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <textarea
          placeholder="Lý do huỷ (bắt buộc)"
          rows={3}
          style={{ width: '100%', padding: 8, marginTop: 8, borderRadius: 6, border: '1px solid #d9d9d9' }}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: 'Xác nhận huỷ',
      okButtonProps: { danger: true },
      cancelText: 'Thoát',
      onOk: async () => {
        if (!reason.trim()) {
          message.error('Vui lòng nhập lý do huỷ');
          throw new Error('reason required');
        }
        try {
          await cancelMutation.mutateAsync({ id: row._id, reason: reason.trim() });
          message.success('Đã huỷ đơn');
        } catch {
          message.error('Huỷ đơn thất bại');
        }
      },
    });
  };

  const handleBulkCancel = () => {
    let reason = '';
    Modal.confirm({
      title: `Huỷ ${selectedRowKeys.length} đơn được chọn?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <textarea
          placeholder="Lý do huỷ áp dụng cho tất cả đơn (bắt buộc)"
          rows={3}
          style={{ width: '100%', padding: 8, marginTop: 8, borderRadius: 6, border: '1px solid #d9d9d9' }}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: 'Xác nhận huỷ',
      okButtonProps: { danger: true },
      cancelText: 'Thoát',
      onOk: async () => {
        if (!reason.trim()) {
          message.error('Vui lòng nhập lý do huỷ');
          throw new Error('reason required');
        }
        const ids = selectedRowKeys.map(String);
        const results = await Promise.allSettled(
          ids.map((id) => cancelMutation.mutateAsync({ id, reason: reason.trim() })),
        );
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const fail = results.length - ok;
        if (fail === 0) {
          message.success(`Đã huỷ ${ok} đơn`);
        } else if (ok === 0) {
          message.error(`Huỷ thất bại tất cả ${fail} đơn`);
        } else {
          message.warning(`Đã huỷ ${ok}/${results.length} đơn — ${fail} đơn thất bại`);
        }
        clearSelection();
      },
    });
  };

  const { data: storeSettings } = useStoreSettings();

  const handlePrintReceipt = async (
    row: TenantOrder,
    kind: 'receipt' | 'invoice' | 'gold_guarantee' = 'receipt',
  ) => {
    const labels: Record<typeof kind, string> = {
      receipt: 'hoá đơn POS',
      invoice: 'hoá đơn VAT',
      gold_guarantee: 'giấy đảm bảo vàng',
    };
    try {
      const res = await printOrderReceipt(
        row,
        {
          storeName: storeSettings?.name,
          storeAddress: storeSettings?.address,
          storePhone: storeSettings?.phone,
        },
        kind,
      );
      if (res && res.method !== 'browser') {
        message.success(`Đã gửi lệnh in ${labels[kind]}`);
      }
    } catch (e: unknown) {
      const err = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(err ?? (e as Error)?.message ?? `In ${labels[kind]} thất bại`);
    }
  };

  const actionColumn = buildOrderActionColumn((key, row) => {
    if (key === 'view') navigate(ROUTES.orders.detail(row._id));
    else if (key === 'cancel') handleCancelOrder(row);
    else if (key.startsWith('print:')) {
      const kind = key.slice('print:'.length) as 'receipt' | 'invoice' | 'gold_guarantee';
      handlePrintReceipt(row, kind);
    }
  });

  const filterConfigs = buildOrderFilters({ drafts, setDraft, onApply: applyFilters });
  const selectedCount = selectedRowKeys.length;

  return (
    <PageShell title={`Đơn hàng (${total})`} fullHeight>
      <OrderSummaryCards
        count_by_channel={data?.summary?.count_by_channel ?? ({} as Record<OrderChannel, number>)}
        active_group={(filters.channel_group as OrderChannelGroup) || undefined}
        onSelectGroup={(g) => {
          // Direct URL update — setDraft + applyFilters bị stale closure khi click cùng tick.
          const next = new URLSearchParams(searchParams);
          if (g) next.set('channel_group', g);
          else next.delete('channel_group');
          next.set('page', '1');
          setSearchParams(next, { replace: true });
        }}
      />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <Button
          size="small"
          icon={<BarChartOutlined />}
          style={{ marginBottom: 8 }}
          onClick={() => setShiftSummaryOpen(true)}
        >
          Tổng kết ca
        </Button>
        <Button
          size="small"
          icon={<SettingOutlined />}
          style={{ marginBottom: 8 }}
          onClick={() => openColumnSettings?.()}
        >
          Cột
        </Button>
        <ExportOrderButton
          filter={{
            channel_group: filters.channel_group,
            status_group: filters.status_group,
            search: filters.search,
            date_from: filters.date_from,
            date_to: filters.date_to,
          }}
        />
        <FilterBar
          filters={filterConfigs}
          activeCount={activeFilterCount}
          collapseToButton
          onApply={applyFilters}
          onClear={clearFilters}
        />
      </div>
      {selectedCount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 10px',
            marginBottom: 8,
            border: '1px solid #f0d7df',
            borderRadius: 8,
            background: '#fff6f8',
          }}
        >
          <Text strong>Đã chọn {selectedCount} đơn hàng</Text>
          <Space size={8}>
            <Button
              size="small"
              danger
              loading={cancelMutation.isPending}
              onClick={handleBulkCancel}
            >
              Huỷ nhiều
            </Button>
            <Button size="small" onClick={clearSelection}>Bỏ chọn</Button>
          </Space>
        </div>
      )}
      <ERPTable<TenantOrder>
        tableKey="orders-list"
        columns={orderColumns}
        actionColumn={actionColumn}
        fitHeight
        bottomOffset={220}
        hideSettingsButton
        registerOpenSettings={(fn) => setOpenColumnSettings(() => fn)}
        dataSource={rows}
        loading={isFetching}
        rowKey="_id"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
        }}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `${t} đơn hàng`,
          onChange: (p, ps) => setTableState({ page: p, pageSize: ps }),
        }}
      />
      <Drawer
        title="Tổng kết ca hiện tại"
        placement="right"
        open={shiftSummaryOpen}
        onClose={() => setShiftSummaryOpen(false)}
        width={520}
        styles={{ body: { padding: 16 } }}
      >
        <OrderShiftSummary totalOrders={total} summary={data?.summary} />
      </Drawer>
    </PageShell>
  );
}
